import React, { useEffect, useState } from 'react';
import api from '../../../../utils/api';
import { toLocalDate } from '../../../../utils/dateUtils';
import {
  RiCalendarCheckLine,
  RiTaskLine,
  RiFileList3Line,
  RiMailLine,
  RiPhoneLine,
  RiBriefcaseLine,
  RiMapPinLine,
  RiArrowRightLine,
} from 'react-icons/ri';

const TASK_STATUS_STYLE = {
  todo:        { dot: '#9CA3AF', label: 'To Do',       bg: '#F9FAFB',              text: '#6B7280' },
  in_progress: { dot: '#0984e3', label: 'In Progress', bg: 'rgba(9,132,227,0.06)', text: '#0984e3' },
  completed:   { dot: '#10b981', label: 'Completed',   bg: 'rgba(16,185,129,0.06)',text: '#10b981' },
};

export default function OverviewTab({ profile, loading: profileLoading, onTabSwitch }) {
  const [tasks,       setTasks]       = useState([]);
  const [leaves,      setLeaves]      = useState([]);
  const [updates,     setUpdates]     = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/tasks/mine'),
      api.get('/leaves/mine'),
      api.get('/daily-updates/mine'),
    ]).then(([t, l, d]) => {
      if (t.status === 'fulfilled') {
        const data = t.value.data?.tasks || t.value.data?.data || t.value.data || [];
        setTasks(Array.isArray(data) ? data : []);
      }
      if (l.status === 'fulfilled') {
        const data = l.value.data?.leaves || l.value.data?.data || l.value.data || [];
        setLeaves(Array.isArray(data) ? data : []);
      }
      if (d.status === 'fulfilled') {
        const data = d.value.data?.updates || d.value.data?.data || d.value.data || [];
        setUpdates(Array.isArray(data) ? data : []);
      }
    }).finally(() => setDataLoading(false));
  }, []);

  const today         = new Date().toDateString();
  const todayUpdates  = updates.filter((u) => new Date(u.createdAt || u.date).toDateString() === today).length;
  const openTasks     = tasks.filter((t) => t.status === 'todo' || t.status === 'in_progress').length;
  const pendingLeaves = leaves.filter((l) => l.status === 'pending').length;
  const approvedDays  = leaves
    .filter((l) => l.status === 'approved')
    .reduce((s, l) => s + (Number(l.total_days) || 0), 0)
    .toFixed(1);

  const recentTasks   = tasks.filter((t) => t.status !== 'completed').slice(0, 4);
  const recentUpdates = [...updates]
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date))
    .slice(0, 3);

  const isHTMLEmpty = (s) =>
    !s || String(s).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length === 0;

  const infoRows = [
    { icon: RiMailLine,      label: 'Email',    value: profile?.email_id || profile?.email },
    { icon: RiPhoneLine,     label: 'Phone',    value: profile?.phone },
    { icon: RiMapPinLine,    label: 'Address',  value: profile?.address },
    { icon: RiBriefcaseLine, label: 'Position', value: profile?.position },
  ];

  const statCards = [
    { icon: RiTaskLine,          label: 'Open Tasks',      value: dataLoading ? null : openTasks,     gradient: 'from-[#0984e3] to-[#00b0ff]', shadow: 'rgba(9,132,227,0.25)',  tab: 'My Tasks' },
    { icon: RiCalendarCheckLine, label: 'Pending Leaves',  value: dataLoading ? null : pendingLeaves, gradient: 'from-[#f59e0b] to-[#fbbf24]', shadow: 'rgba(245,158,11,0.25)', tab: 'Leave Request' },
    { icon: RiFileList3Line,     label: "Today's Updates", value: dataLoading ? null : todayUpdates,  gradient: 'from-[#7c3aed] to-[#d500f9]', shadow: 'rgba(213,0,249,0.25)', tab: 'Daily Updates' },
    { icon: RiCalendarCheckLine, label: 'Approved Days',   value: dataLoading ? null : approvedDays,  gradient: 'from-[#10b981] to-[#34d399]', shadow: 'rgba(16,185,129,0.25)', tab: 'Leave Request' },
  ];

  return (
    <div className="space-y-5">

      {/* Profile hero */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)]">
        <div
          className="h-28 w-full relative rounded-t-2xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#0984e3 0%,#00b0ff 55%,#d500f9 100%)' }}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%,#fff 1px,transparent 1px),radial-gradient(circle at 80% 20%,#fff 1px,transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="px-6 pb-6">
          <div className="-mt-9 mb-3 flex items-end justify-between">
            <div
              className="w-[72px] h-[72px] rounded-2xl border-4 border-white flex items-center justify-center text-white text-2xl font-bold shadow-lg flex-shrink-0 relative z-10"
              style={{ background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' }}
            >
              {(profile?.name || profile?.email_id || profile?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <span className="badge mb-1">{profile?.role || 'Employee'}</span>
          </div>
          {profileLoading ? (
            <div className="space-y-2">
              <div className="h-6 bg-[#F3F4F6] rounded w-1/3 animate-pulse" />
              <div className="h-4 bg-[#F3F4F6] rounded w-1/2 animate-pulse" />
            </div>
          ) : (
            <>
              <h2 className="text-[#1F2937] text-xl font-bold">{profile?.name || '—'}</h2>
              <p className="text-[#6B7280] text-sm mt-0.5">
                {[profile?.position, profile?.username].filter(Boolean).join(' · ') || '—'}
              </p>
            </>
          )}
        </div>
        <div className="border-t border-[#F3F4F6] px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {infoRows.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[rgba(9,132,227,0.08)] flex items-center justify-center flex-shrink-0">
                <Icon size={14} className="text-[#0984e3]" />
              </div>
              <div className="min-w-0">
                <p className="text-[#9CA3AF] text-xs">{label}</p>
                {profileLoading
                  ? <div className="h-3.5 bg-[#F3F4F6] rounded w-20 animate-pulse mt-0.5" />
                  : <p className="text-[#1F2937] text-sm font-medium truncate">{value || '—'}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {statCards.map(({ icon: Icon, label, value, gradient, shadow, tab }) => (
          <button
            key={label}
            onClick={() => onTabSwitch?.(tab)}
            className="bg-white border border-[#E5E7EB] rounded-2xl p-5 shadow-[0_4px_20px_rgba(15,23,42,0.06)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.1)] hover:-translate-y-0.5 transition-all text-left cursor-pointer group"
          >
            <div
              className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`}
              style={{ boxShadow: `0 6px 16px ${shadow}` }}
            >
              <Icon size={18} className="text-white" />
            </div>
            {value === null
              ? <div className="h-7 w-12 bg-[#F3F4F6] rounded animate-pulse mb-1" />
              : <p className="text-[#1F2937] text-2xl font-bold">{value}</p>}
            <p className="text-[#9CA3AF] text-xs mt-0.5 group-hover:text-[#6B7280] transition-colors">{label}</p>
          </button>
        ))}
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">

        {/* Recent tasks */}
        <div className="xl:col-span-3 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
            <h3 className="text-[#1F2937] font-bold">My Active Tasks</h3>
            <button
              onClick={() => onTabSwitch?.('My Tasks')}
              className="text-xs text-[#0984e3] font-semibold hover:underline flex items-center gap-1 cursor-pointer"
            >
              View all <RiArrowRightLine size={12} />
            </button>
          </div>
          {dataLoading ? (
            <div className="divide-y divide-[#F3F4F6]">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="px-6 py-4 space-y-2">
                  <div className="h-4 bg-[#F3F4F6] rounded w-2/5 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-3/5 animate-pulse" />
                </div>
              ))}
            </div>
          ) : recentTasks.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2">
              <RiTaskLine size={28} className="text-[#E5E7EB]" />
              <p className="text-[#9CA3AF] text-sm">No active tasks.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F3F4F6]">
              {recentTasks.map((t) => {
                const cfg    = TASK_STATUS_STYLE[t.status] || TASK_STATUS_STYLE.todo;
                const due    = t.due_date || t.dueDate;
                const overdue = due && t.status !== 'completed' && toLocalDate(new Date(due)) < toLocalDate(new Date());
                return (
                  <div key={t._id || t.id} className="px-6 py-4 hover:bg-[#FAFBFC] transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: cfg.dot }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#1F2937] text-sm font-semibold truncate">{t.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.text }}>
                            {cfg.label}
                          </span>
                          {due && (
                            <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-500 font-semibold' : 'text-[#9CA3AF]'}`}>
                              <RiCalendarCheckLine size={10} />
                              {overdue ? 'Overdue · ' : ''}
                              {new Date(due).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent updates */}
        <div className="xl:col-span-2 bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
            <h3 className="text-[#1F2937] font-bold">Recent Updates</h3>
            <button
              onClick={() => onTabSwitch?.('Daily Updates')}
              className="text-xs text-[#0984e3] font-semibold hover:underline flex items-center gap-1 cursor-pointer"
            >
              View all <RiArrowRightLine size={12} />
            </button>
          </div>
          {dataLoading ? (
            <div className="divide-y divide-[#F3F4F6]">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="px-6 py-4 space-y-2">
                  <div className="h-3 bg-[#F3F4F6] rounded w-1/4 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-full animate-pulse" />
                </div>
              ))}
            </div>
          ) : recentUpdates.length === 0 ? (
            <div className="flex flex-col items-center py-10 gap-2">
              <RiFileList3Line size={28} className="text-[#E5E7EB]" />
              <p className="text-[#9CA3AF] text-sm">No updates yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#F3F4F6]">
              {recentUpdates.map((u, i) => {
                const isToday    = new Date(u.createdAt || u.date).toDateString() === today;
                const completed  = u.completed   || '';
                const inProgress = u.in_progress || u.inProgress || '';
                const notes      = u.notes       || '';
                const legacy     = u.content     || u.text       || '';
                const hasNew     = !isHTMLEmpty(completed) || !isHTMLEmpty(inProgress) || !isHTMLEmpty(notes);

                const Section = ({ color, label, value }) => {
                  if (isHTMLEmpty(value)) return null;
                  return (
                    <div className="flex gap-2">
                      <div className="w-0.5 rounded-full flex-shrink-0" style={{ background: color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color }}>{label}</p>
                        <div
                          className="rich-content text-[#1F2937] text-xs leading-relaxed line-clamp-3"
                          dangerouslySetInnerHTML={{ __html: value }}
                        />
                      </div>
                    </div>
                  );
                };

                return (
                  <div key={u._id || u.id || i} className="px-6 py-4 hover:bg-[#FAFBFC] transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(135deg,#0984e3,#d500f9)' }} />
                      <p className="text-[#9CA3AF] text-xs">
                        {new Date(u.createdAt || u.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      {isToday && (
                        <span className="badge text-[#10b981] bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.2)] text-[10px]">Today</span>
                      )}
                    </div>
                    {hasNew ? (
                      <div className="space-y-2 pl-3.5">
                        <Section color="#10b981" label="Completed"   value={completed} />
                        <Section color="#0984e3" label="In Progress" value={inProgress} />
                        <Section color="#7c3aed" label="Notes"       value={notes} />
                      </div>
                    ) : (
                      <p className="text-[#1F2937] text-xs leading-relaxed line-clamp-3 pl-3.5 whitespace-pre-wrap">
                        {legacy || '—'}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="px-6 py-3 border-t border-[#F3F4F6]">
            <button
              onClick={() => onTabSwitch?.('Daily Updates')}
              className="w-full text-xs font-semibold text-[#0984e3] hover:text-[#0870c2] transition-colors cursor-pointer text-center"
            >
              + Submit Today's Update
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
